import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Row, Col, Select, Divider, Button, message, Spin } from 'antd';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;
const { Option } = Select;

const OrganisationWideDefaults = () => {
  const [defaultAccess, setDefaultAccess] = useState({});
  const [originalAccess, setOriginalAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const security = useSelector((state) => state.data.items[0]);

  useEffect(() => {
    fetchOrganisationWideDefaults();
  }, []);

  useEffect(() => {
    // Check if there are any changes compared to original values
    const changed = Object.keys(defaultAccess).some(key => 
      defaultAccess[key] !== originalAccess[key]
    );
    setHasChanges(changed);
  }, [defaultAccess, originalAccess]);

  const fetchOrganisationWideDefaults = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/org_wide_defaults`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      
      const response = await apiService.makeCall();
      
      if (response && response.success && response.org_wide_defaults) {
        // Store API values directly without transformation
        const formattedDefaults = {};
        response.org_wide_defaults.forEach(item => {
          formattedDefaults[item.object_name] = item.default_access;
        });
        
        setDefaultAccess(formattedDefaults);
        setOriginalAccess(formattedDefaults); // Store the original values to track changes
      } else {
        message.error('Failed to load organization-wide defaults.');
      }
    } catch (error) {
      console.error('Error fetching organization-wide defaults:', error);
      message.error('Failed to load organization-wide defaults.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (object, value) => {
    console.log(`Changed ${object} to ${value}`);
    setDefaultAccess(prev => ({
      ...prev,
      [object]: value,
    }));
    setHasChanges(true); 
  };

  const handleCancel = () => {
    // Reset to original values
    setDefaultAccess({...originalAccess});
    message.info('Changes discarded');
  };

  const handleSave = async () => {
    if (!hasChanges) {
      message.info('No changes to save');
      return;
    }
    
    setLoading(true);
    try {
      // Format data for API - no transformation needed now
      const updatedDefaults = Object.keys(defaultAccess).map(objectName => ({
        object_name: objectName,
        default_access: defaultAccess[objectName]
      }));
      
      console.log('Updated Defaults:', updatedDefaults);
      const apiService = new ApiService(
        `${BASE_URL}/org_wide_defaults/batch_update`,
        { 'Content-Type': 'application/json' },
        'PUT',
        { defaults: updatedDefaults }
      );
      
      const response = await apiService.makeCall();
      
      if (response && response.success) {
        message.success('Organization-wide defaults updated successfully.');
        fetchOrganisationWideDefaults(); // Refresh data
      } else {
        message.error('Failed to update organization-wide defaults.');
        if (response && response.errors) {
          console.error('API errors:', response.errors);
        }
      }
    } catch (error) {
      console.error('Error updating organization-wide defaults:', error);
      message.error('Failed to update organization-wide defaults.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card style={{ maxWidth: '100%', margin: '32px auto', borderRadius: 8 }}>
  <Spin spinning={loading}>
    <Title level={3}>Organization-Wide Defaults</Title>
    <Text type="secondary">
      Organization-wide defaults specify the default level of access users have to each other's records. 
      You can further restrict access using roles, sharing rules, and manual sharing.
    </Text>
    <Divider />

    <Row gutter={16} style={{ fontWeight: 'bold', marginBottom: 8 }}>
      <Col span={8}>Object</Col>
      <Col span={16}>Default Access</Col>
    </Row>

    <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
      {Object.keys(defaultAccess).length > 0 ? (
        Object.keys(defaultAccess).map(object => (
          <Row key={object} gutter={16} align="middle" style={{ marginBottom: 12 }}>
            <Col span={8}><Text>{object}</Text></Col>
            <Col span={16}>
              <Select
                value={defaultAccess[object]}
                style={{ width: 200 }}
                onChange={value => handleChange(object, value)}
                disabled={security?.system_settings?.organisation_wide_defaults?.edit} // Disable if no changes
              >
                <Option value="private">Private</Option>
                <Option value="public_read">Public Read Only</Option>
                <Option value="public_read_write">Public Read/Write</Option>
              </Select>
            </Col>
          </Row>
        ))
      ) : (
        <Row>
          <Col span={24}>
            <Text type="secondary">No objects found. Please create objects first.</Text>
          </Col>
        </Row>
      )}
    </div>

    <Divider />
    <Row justify="end" gutter={8}>
      <Col>
        <Button 
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </Button>
      </Col>
      <Col>
        <Button 
          type="primary" 
          onClick={handleSave}
          disabled={!hasChanges || Object.keys(defaultAccess).length === 0}
        >
          Save
        </Button>
      </Col>
    </Row>
  </Spin>
</Card>

  );
};

export default OrganisationWideDefaults;